/*
 * File: LoginForm.js
 * Project: mp-mobile-app
 *
 * Created by Rob Rothe
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Handles the login form
 */
import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import UIProgress from "components/ui/UIProgress";
import LogoutHandler from "../../handlers/LogoutHandler";
import LoginStore from "services/login/Store";
import clsx from "clsx";

const useStyles = makeStyles(theme => ({
  button: {
    marginTop: theme.spacing(2.5),
    width: "90%"
  },
  logoutMessage: {
    fontSize: "1em",
    fontWeight: 300,
    textAlign: "center"
  },
  errorMessage: {
    color: theme.palette.error.main
  },
  loginFields: {
    width: "90%"
  },
  loginForm: {
    textAlign: "center"
  }
}));

const LoginForm = ({
  onSubmit,
  loggingIn,
  error,
  showLogout,
  wasAutoLoggedOut
}) => {
  const classes = useStyles();

  //checks local storage for any error messages that were saved from logging out
  const checkError = () => {
    const errorMessage = localStorage.getItem("error");
    return errorMessage;
  };

  //checks to see what error should be passed into login form, if any.
  if (!error) {
    error = checkError();
    localStorage.removeItem("error", true);
  }

  //Handles auto-logging out
  if (wasAutoLoggedOut) {
    LoginStore.logout("Timer expired");
  }
  //checks on what kind of logout event happened. Uses non-error styling it manually logged out. Uses error styling if timed out/API error.
  const useErrorStyle = error !== "Logged Out Successfully";

  return (
    <React.Fragment>
      {loggingIn && <UIProgress fullScreen />}
      {error && (
        <Typography
          className={clsx(
            classes.logoutMessage,
            useErrorStyle && classes.errorMessage
          )}
        >
          {error}
        </Typography>
      )}

      <form
        className={classes.loginForm}
        noValidate
        autoComplete="off"
        onSubmit={onSubmit}
      >
        <TextField
          className={classes.loginFields}
          fullWidth={true}
          margin="normal"
          type="text"
          name="username"
          label="Username"
          autoCorrect="off"
          inputProps={{
            autoCapitalize: "none"
          }}
          id="username"
          autoComplete="username"
          variant="filled"
        />
        <TextField
          className={classes.loginFields}
          margin="normal"
          type="password"
          name="password"
          label="Password"
          id="password"
          autoComplete="current-password"
          variant="filled"
        />
        <Button
          id="submit-login"
          disabled={loggingIn}
          className={classes.button}
          variant="contained"
          type="submit"
          color="secondary"
        >
          Log In
        </Button>
        {showLogout && (
          <LogoutHandler>
            <Button fullWidth className={classes.button}>
              SWITCH USER
            </Button>
          </LogoutHandler>
        )}
      </form>
    </React.Fragment>
  );
};

LoginForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  wasAutoLoggedOut: PropTypes.bool,
  loggingIn: PropTypes.bool,
  error: PropTypes.node,
  showLogout: PropTypes.bool
};

export default LoginForm;
